import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '@lib/msalConfig';
import { trpc } from '@lib/trpc';
import toast from 'react-hot-toast';
import { clearAssignedTo } from '@utils/assignedToStorage';

interface User {
  id: string;
  email: string;
  username: string;
  name: string | null;
  isAdmin?: boolean;
  roles?: {
    id: string;
    name: string;
    displayName: string;
    color?: string;
  }[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  passwordChangeRequired: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; passwordChangeRequired?: boolean }>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; token?: string; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  
  // Initialize token from localStorage
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    setAuthToken(token);
    setInitialized(true);
  }, []);

  // tRPC mutations and utils
  const utils = trpc.useContext();
  const loginMutation = trpc.auth.login.useMutation();
  const logoutMutation = trpc.auth.logout.useMutation();
  const changePasswordMutation = trpc.auth.changePassword.useMutation();
  
  // Only validate session if we have a token and are initialized
  const validateSessionQuery = trpc.auth.validateSessionWithRoles.useQuery(
    { token: authToken || '' },
    {
      enabled: initialized && !!authToken,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (_data) => {
        // Session validation successful
      },
      onError: (_error) => {
        // Session validation failed
      },
      onSettled: (_data, _error) => {
        // Session validation completed
      }
    }
  );

  // Check for existing session on app load
  useEffect(() => {
    if (!initialized) return;
    
    // Session validation effect triggered
    
    // If we have a token but the query is still loading, keep showing loading
    if (authToken && validateSessionQuery.isLoading) {
      // Token exists but query loading, staying in loading state
      setIsLoading(true);
      return;
    }
    
    if (authToken && validateSessionQuery.data?.user) {
      // Valid session data found, setting user
      // Merge user data with roles from the response
      const userWithRoles = {
        ...validateSessionQuery.data.user,
        roles: validateSessionQuery.data.roles || []
      };
      setUser(userWithRoles);
      setIsLoading(false);
    } else if (validateSessionQuery.isError || (validateSessionQuery.data && !validateSessionQuery.data.user)) {
      // Session invalid, clearing token and logging out
      // Clear invalid token
      localStorage.removeItem('auth_token');
      setAuthToken(null);
      setUser(null);
      setIsLoading(false);
    } else if (!authToken) {
      // No token, not loading
      // No token, not loading
      setIsLoading(false);
    }
  }, [initialized, authToken, validateSessionQuery.data, validateSessionQuery.isError, validateSessionQuery.isLoading]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    
    try {
      const result = await loginMutation.mutateAsync({ email, password });
      
      if (result.user && result.token) {
        setUser(result.user);
        localStorage.setItem('auth_token', result.token);
        setAuthToken(result.token);
        
        // Check if password change is required
        const pwdChangeRequired = result.passwordChangeRequired || false;
        setPasswordChangeRequired(pwdChangeRequired);
        
        return { success: true, passwordChangeRequired: pwdChangeRequired };
      }
      return { success: false };
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const result = await changePasswordMutation.mutateAsync({
        currentPassword,
        newPassword,
      });

      if (result.success && result.token) {
        // Update auth token
        localStorage.setItem('auth_token', result.token);
        setAuthToken(result.token);
        
        // Password change is complete
        setPasswordChangeRequired(false);
        
        // Only invalidate necessary queries based on user role
        utils.auth.validateSessionWithRoles.invalidate();
        
        // Skip admin data invalidation for non-admin users
        const hasAdminRole = user?.roles?.some(role => role.name === 'admin') || user?.isAdmin;
        if (hasAdminRole) {
          // Only invalidate specific user queries that admins have access to
          try {
            utils.admin.getAllUsers.invalidate();
          } catch (error) {
            // Silently handle if admin queries are not accessible
            // Could not invalidate admin queries - silently handle
          }
        }
        
        // Show success toast
        toast.success('Password changed successfully');
        return { success: true, token: result.token };
      }
      
      toast.error('Password change failed');
      return { success: false, error: 'Password change failed' };
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to change password';
      toast.error(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };


  const logout = async (): Promise<void> => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await logoutMutation.mutateAsync();
      }
      
      // Clear local state
      setUser(null);
      localStorage.removeItem('auth_token');
      setAuthToken(null);
      
      // Clear persisted filter values
      clearAssignedTo();
      
      // Clear cache based on user role
      utils.status.invalidate(); // Clear all status-related cache
      utils.invoice.invalidate(); // Clear all invoice-related cache
      
      // Only clear admin-related cache if user was admin
      const wasAdmin = user?.roles?.some(role => role.name === 'admin') || user?.isAdmin;
      if (wasAdmin) {
        utils.user.invalidate(); // Only clear user-related cache for admins
      }
      
      // Clear backend cache as well
      try {
        await utils.status.clearCache.mutate();
      } catch (error) {
        // Silently handle backend cache clearing errors
      }
      
      // Clear the entire query cache to be safe
      utils.invalidate();
      
      // Logout from Azure AD if applicable
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await msalInstance.logoutPopup();
      }
    } catch (error: any) {
      // Logout error - silently handle
      // Still clear local state and cache even if backend logout fails
      setUser(null);
      localStorage.removeItem('auth_token');
      setAuthToken(null);
      
      // Clear persisted filter values
      clearAssignedTo();
      
      // Clear cache even on error
      utils.invalidate();
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    passwordChangeRequired,
    login,
    logout,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
